import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type LifecycleOfResult, type LifecycleReportResult } from '@whinerp/client';
import { formatDate, todayIn } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * The lifecycle stage (G-171): where somebody is, subscriber to advocate, with
 * the date of each stage reached. Read from what happened, never typed.
 */

const STAGES = ['subscriber', 'lead', 'mql', 'sql', 'customer', 'advocate'] as const;

export function LifecyclePanel({ objectKey, recordId }: { objectKey: 'lead' | 'contact'; recordId: string }): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const enabled = useEntitlement('F-063').enabled;
  const life = useQuery<LifecycleOfResult, ApiError>({
    queryKey: ['lifecycle', objectKey, recordId],
    queryFn: () => api.lifecycleOf({ path: { objectKey, recordId } }),
    enabled,
  });
  if (!enabled || !life.data) return null;
  const view = life.data;
  return (
    <Panel data-region="lifecycle">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('lifecycle.title')}</SectionLabel>
        <ol className="flex flex-wrap gap-2 text-dense" data-lifecycle-stage={view.stage}>
          {STAGES.map((stage) => {
            const at = view.reached[stage];
            const current = stage === view.stage;
            return (
              <li key={stage} data-lifecycle-step={stage} data-reached={at !== null}
                className={`rounded-chip px-2 py-1 ${current ? 'bg-bronze text-surface' : at ? 'bg-sunken text-ink' : 'text-ink-300'}`}>
                {t(`lifecycle.stage.${stage}`)}
                {at ? <span className="ms-1 text-meta">{formatDate(at, format)}</span> : null}
              </li>
            );
          })}
        </ol>
      </div>
    </Panel>
  );
}

/** Where the people who arrived in a period have got to (G-171). */
export function LifecycleReportPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = useEntitlement('F-063').enabled && can('analytics.view');
  const today = todayIn(format);
  const [days, setDays] = useState(90);
  const start = new Date(Date.parse(today) - days * 86_400_000).toISOString().slice(0, 10);
  const report = useQuery<LifecycleReportResult, ApiError>({
    queryKey: ['lifecycle-report', start, today],
    queryFn: () => api.lifecycleReport({ query: { periodStart: start, periodEnd: today } }),
    enabled,
    placeholderData: (previous) => previous,
  });
  if (!enabled || !report.data) return null;
  const view = report.data;
  return (
    <Panel data-region="lifecycle-report">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('lifecycle.reportTitle')}</SectionLabel>
          <select className="ms-auto h-input rounded-control border border-control bg-surface px-2 text-body text-ink" data-lifecycle-days
            aria-label={t('lifecycle.period')} value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {[30, 90, 365].map((value) => <option key={value} value={value}>{t('lifecycle.lastDays', { values: { days: String(value) } })}</option>)}
          </select>
        </div>
        <ul className="flex flex-wrap gap-3 text-dense">
          {STAGES.map((stage) => (
            <li key={stage} data-lifecycle-count={stage}>
              <span className="text-ink-meta">{t(`lifecycle.stage.${stage}`)}</span> <span className="text-ink">{view.byStage[stage]}</span>
            </li>
          ))}
        </ul>
        <ul className="flex flex-col gap-1 text-dense" data-lifecycle-conversion>
          {view.conversion.map((step) => (
            <li key={step.from} data-lifecycle-move={step.from}>
              {t('lifecycle.move', {
                values: {
                  from: t(`lifecycle.stage.${step.from}`),
                  to: t(`lifecycle.stage.${step.to}`),
                  went: String(step.went),
                  reached: String(step.reached),
                  days: step.medianDays === null ? '-' : String(step.medianDays),
                },
              })}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
